import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/atoms/button";
import ModalCounterBlock from "./modalCounterBlock";

import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useAttemptQuestionMutation,
  useSaveCodeMutation,
  useSaveSubjectiveMutation,
  useStartQuestionMutation,
  useStartSectionMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import GetHeroIcon from "@assessment/utils/getHeroIcons";

import { cn } from "../../../../utils/cn";

import { OngoingSection } from "../../../../types/assessmentOngoing";
import { useShowErrors } from "../toasterWrapper";
import { extractPlainText, saveAnswers } from "../saveAnswers";

interface SectionSummaryModalProps {
  targetSection: OngoingSection;
  // updateTimeSpentOnSection?: any;
  // updateTimeSpentOnLastAttemptedQuestion?: any;
}

const SectionSummaryModal = ({
  targetSection
  // updateTimeSpentOnSection,
  // updateTimeSpentOnLastAttemptedQuestion
}: SectionSummaryModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [startSection] = useStartSectionMutation();

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const assessmentProgress = useAppSelector(
    (state) => state.AssessmentSlice.assessmentProgress
  );

  const [focusedType, setFocusTyped] = useState<
    null | "total" | "completed" | "flagged" | "unanswered"
  >(null);
  const [disableFunc, setDisableFunc] = useState(false);
  const [message, setMessage] = useState("");

  const sectionProgress = assessmentProgress[targetSection.sectionId];
  const sectionStatus = sectionProgress.status;

  const sectionProgressQuestionsValueList = Object.values(
    sectionProgress.questions
  );

  const totalCompletedQuestions = sectionProgressQuestionsValueList.filter(
    (question) => question.status === "completed"
  ).length;
  const totalFlaggedQuestions = sectionProgressQuestionsValueList.filter(
    (question) => question.flagged
  ).length;
  const totalUnattemptedQuestions = sectionProgressQuestionsValueList.filter(
    (question) =>
      question.status === "not_started" || question.status === "unanswered"
  ).length;
  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );
  const responses = useAppSelector(
    (store) => store.AssessmentSlice.candidateResponse
  );
  const responsesFib = useAppSelector(
    (store) => store.AssessmentSlice.candidateFibResponse
  );
  const assessmentData = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData
  );
  const ongoingSections = assessmentData?.ongoingSections;

  const [attemptQuestion] = useAttemptQuestionMutation();
  const [startQuestion] = useStartQuestionMutation();
  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [saveCodeAPi] = useSaveCodeMutation();
  const [currentQuestionType, setCurrentQuestionType] = useState<any>(null);

  useEffect(() => {
    if (
      ongoingSections &&
      activeQuestionMeta &&
      activeQuestionMeta?.sectionId
    ) {
      setMessage("");

      const targetSection = ongoingSections.find(
        (section) => section.sectionId === activeQuestionMeta.sectionId
      );
      const currentQn = targetSection?.questions.filter(
        (el: any) => el.questionId == activeQuestionMeta?.questionId
      );

      const currentQnType = currentQn?.[0]?.type;
      setCurrentQuestionType(currentQnType);
      if (currentQnType == "subjective") {
        const subType = currentQn?.[0]?.submissionType?.[0];
        const subjectiveCandidateRes: any = activeQuestionMeta?.questionId
          ? responses[activeQuestionMeta.questionId!]
          : undefined;

        if (
          subType == "text" &&
          subjectiveCandidateRes &&
          subjectiveCandidateRes?.value &&
          extractPlainText(subjectiveCandidateRes?.value)
            .trim()
            .split(/\s+/)
            .filter(Boolean).length < 40
        )
          setMessage(
            t("assessment.sectionSummary.lowWordCountWarning", {
              questionTitle: currentQn?.[0]?.questiontitle
            })
          );
        else if (
          subType == "video" &&
          subjectiveCandidateRes &&
          subjectiveCandidateRes?.[0]?.length > 0
        )
          setMessage(
            t("assessment.sectionSummary.videoNotSubmittedWarning", {
              questionTitle: currentQn?.[0]?.questiontitle
            })
          );
      }
    }
  }, [ongoingSections, activeQuestionMeta, activeQuestionMeta?.sectionId]);

  const finishSectionAndUpdateViewHandler = async () => {
    setDisableFunc(true);
    try {
      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const allQuestionsInTheSectionAreCompleted = Object.values(
        assessmentProgress[targetSection.sectionId].questions
      ).every((question) => question.status === "completed");

      if (allQuestionsInTheSectionAreCompleted) {
        const res = await startSection({
          actionType: "completed",
          ongoingId: ongoingId,
          sectionId: targetSection.sectionId
        });

        if (res.error) {
          throw {
            status: "",
            title: "Failed to complete the section",
            err: res.error,
            description: ""
          };
        }
      }

      // const res2 = await updateTimeSpentOnSection();
      // await updateTimeSpentOnLastAttemptedQuestion();

      // if (!res2) {
      //   throw {
      //     status: "",
      //     title: "Failed to update the time spent on section",
      //     err: {},
      //     description: ""
      //   };
      // }

      if (allQuestionsInTheSectionAreCompleted) {
        dispatch(
          AssessmentSliceActions.mutateSectionProgress({
            sectionId: targetSection.sectionId,
            status: "completed"
          })
        );
      } else {
        dispatch(
          AssessmentSliceActions.mutateSectionProgress({
            sectionId: targetSection.sectionId,
            status: "in_progress"
          })
        );
      }

      dispatch(AssessmentSliceActions.mutateShowSummaryModal(false));
      dispatch(AssessmentSliceActions.mutateAssessmentView("dashboard"));
      dispatch(AssessmentSliceActions.mutateActiveQuestionMeta(null));
      setDisableFunc(false);
    } catch (err) {
      console.error(err);
      setDisableFunc(false);
    }

    currentQuestionType &&
      currentQuestionType == "subjective" &&
      dispatch(
        AssessmentSliceActions.mutateCandidateResponse({
          questionId: activeQuestionMeta?.questionId,
          answerKey: []
        })
      );
  };

  const handleClick = (question: any) => {
    dispatch(
      AssessmentSliceActions.mutateActiveQuestionMeta({
        questionId: question.questionId,
        sectionId: question.sectionId
      })
    );
    dispatch(AssessmentSliceActions.mutateAssessmentView("question"));
    dispatch(AssessmentSliceActions.mutateShowSummaryModal(false));
  };

  return (
    <div>
      {/* Progress */}
      <div className="mt-8">
        <div className="w-full h-1.5 bg-border flex flex-row rounded-md">
          <div
            className=" bg-[#00875D] rounded-md h-1.5"
            style={{
              width: `${Math.round(
                (totalCompletedQuestions / targetSection.totalQuestionCount) *
                  100
              )}%`
            }}
          />
          <div
            className=" bg-[#FDF1D0] rounded-md h-1.5"
            style={{
              width: `${Math.round(
                (totalFlaggedQuestions / targetSection.totalQuestionCount) * 100
              )}%`
            }}
          />
        </div>
      </div>
      {/* Counter Cubes */}
      <div className="flex flex-wrap items-center justify-start gap-8 mt-10">
        <ModalCounterBlock
          title={t("assessment.sectionSummary.totalQuestions")}
          count={targetSection.totalQuestionCount}
          variant="total"
          setFocusTyped={setFocusTyped}
        />
        <ModalCounterBlock
          title={t("assessment.sectionSummary.completedQuestions")}
          count={totalCompletedQuestions}
          variant="completed"
          setFocusTyped={setFocusTyped}
        />
        <ModalCounterBlock
          title={t("assessment.sectionSummary.flaggedQuestions")}
          count={totalFlaggedQuestions}
          variant="flagged"
          setFocusTyped={setFocusTyped}
        />
        <ModalCounterBlock
          title={t("assessment.sectionSummary.unattemptedQuestions")}
          count={totalUnattemptedQuestions}
          variant="unanswered"
          setFocusTyped={setFocusTyped}
        />
      </div>
      {/* Section Info */}
      <div className="px-6 py-3 mt-10 border border-border rounded-xl bg-bkg">
        {/* Top */}
        <div className="flex flex-col items-start justify-between gap-5 md:items-center md:flex-row lg:gap-10">
          <div
            className={cn(
              "flex items-center justify-between px-5 py-2 border-l-8 rounded",
              sectionStatus === "completed" && "border-[#00875D]",
              sectionStatus === "not_started" && "border-secondary",
              sectionStatus === "in_progress" && "border-amber-500"
            )}
          >
            <div className="flex flex-col gap-2">
              <p className="text-base font-medium leading-5 text-secondary/70">
                {t("assessment.sectionCard.section")} {targetSection.orderInQp}
              </p>
              <h2 className="text-xl leading-6 ont-medium text-secondary">
                {targetSection.title}
              </h2>
            </div>
          </div>
        </div>
        <div className="mt-5">
          {message?.length > 0 && (
            <div className="bg-[#FEFCE8] px-4 py-3 rounded border flex justify-between items-center gap-3 mt-3">
              <GetHeroIcon
                name="ExclamationTriangleIcon"
                className="w-5 h-5 text-[#854D0E]"
                variant="outline"
              />
              <span className="text-sm font-medium text-[#854D0E] flex-1">
                {message}
              </span>
            </div>
          )}
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {targetSection.questionTypeCountMap.coding > 0 && (
              <span className="text-base font-semibold text-secondary/50">
                {t("assessment.sectionSummary.codingQuestions")}{" "}
                {targetSection.questionTypeCountMap.coding}
              </span>
            )}
            {targetSection.questionTypeCountMap.objective > 0 && (
              <span className="text-base font-semibold text-secondary/50">
                {t("assessment.sectionSummary.objectiveQuestions")}{" "}
                {targetSection.questionTypeCountMap.objective}
              </span>
            )}
          </div>
          {/* Questions  */}
          <div className="flex flex-wrap items-center justify-start gap-5 mb-5 h-32 overflow-y-auto thin-scrollbar">
            {targetSection.questions.map((question) => {
              const questionBlockStatus =
                sectionProgress.questions[question.questionId].status ===
                "not_started"
                  ? "unanswered"
                  : sectionProgress.questions[question.questionId].status;
              const questionBlockFlagged =
                sectionProgress.questions[question.questionId].flagged;

              return (
                <div
                  onClick={() => handleClick(question)}
                  key={question.id}
                  className={cn(
                    "relative w-16 h-16 rounded-xl flex justify-center items-center transition-all duration-200 ease-in-out cursor-pointer",
                    questionBlockStatus === "completed" &&
                      "bg-[#F0FDF4] text-[#00875D]",
                    questionBlockFlagged &&
                      questionBlockStatus !== "completed" &&
                      "bg-amber-100/70 text-amber-500",
                    questionBlockStatus === "unanswered" &&
                      "bg-secondary-bg text-secondary",
                    questionBlockStatus === "not_started" &&
                      "bg-secondary-bg text-secondary",
                    focusedType === questionBlockStatus &&
                      "shadow-inner border border-gray-500",
                    focusedType === questionBlockStatus &&
                      questionBlockStatus === "completed" &&
                      "shadow-inner border-[#00875D] border-2",
                    focusedType === "flagged" &&
                      questionBlockFlagged &&
                      "shadow-inner border border-gray-500"
                  )}
                >
                  {/* Flag Icon - Outside the number */}
                  {questionBlockFlagged && (
                    <GetHeroIcon
                      name="FlagIcon"
                      className={cn(
                        "absolute bottom-5 left-7 w-4 h-4 text-[#AA6207] drop-shadow-sm",
                        focusedType === questionBlockStatus &&
                          "animate-[slideIn_0.3s_ease-out]"
                      )}
                    />
                  )}

                  {/* Number */}
                  <span
                    className={cn(
                      "text-2xl font-semibold leading-7 transition-all duration-200 ease-in-out",
                      focusedType === questionBlockStatus && "text-3xl",
                      focusedType === "flagged" &&
                        questionBlockFlagged &&
                        "text-3xl"
                    )}
                  >
                    {question.orderInSection}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Button */}
      <div className="flex items-center justify-end gap-5 mt-8">
        <Button
          variant="outlined"
          buttonColor="secondary"
          onClick={() => {
            dispatch(AssessmentSliceActions.mutateShowSummaryModal(false));
          }}
          disabled={disableFunc}
        >
          {t("common.close")}
        </Button>
        <Button
          variant="contained"
          buttonColor="primary"
          onClick={() => {
            finishSectionAndUpdateViewHandler();
            AssessmentSliceActions.mutateExpandAll(true);
          }}
          disabled={disableFunc}
        >
          {t("assessment.sectionSummary.goToSections")}
        </Button>
      </div>
    </div>
  );
};

export default SectionSummaryModal;
